import {
  AppWindow,
  ArrowRight,
  Cloud,
  Database,
  FileText,
  Lock,
  Network,
  Search,
  Server,
  Workflow,
} from "lucide-react";
import type { ComponentType } from "react";

type Item = { icon: ComponentType<{ className?: string }>; label: string };

const sources: Item[] = [
  { icon: AppWindow, label: "Microsoft 365 & Google Workspace" },
  { icon: Server, label: "ERPs, EMRs & databases" },
  { icon: Workflow, label: "Legacy & custom apps" },
];

// The three things the automation layer actually does, in plain language.
const layer: Item[] = [
  { icon: Network, label: "Connect", },
  { icon: Workflow, label: "Automate" },
  { icon: Search, label: "Retrieve" },
];

const destinations: Item[] = [
  { icon: FileText, label: "Document management" },
  { icon: Cloud, label: "Cloud storage" },
  { icon: Database, label: "Databases & archives" },
];

// A directional arrow that points right on desktop and down when the flow
// stacks on smaller screens.
function FlowArrow() {
  return (
    <div className="flex items-center justify-center py-1 lg:py-0" aria-hidden="true">
      <div className="flex h-9 w-9 items-center justify-center rounded-full border border-borderSubtle bg-surface text-primary shadow-sm">
        <ArrowRight className="h-4 w-4 rotate-90 lg:rotate-0" />
      </div>
    </div>
  );
}

export default function ArchitectureDiagram() {
  return (
    <section aria-labelledby="architecture-heading">
      <div className="rounded-3xl border border-borderSubtle bg-surface p-4 sm:p-6 lg:p-8 shadow-xl shadow-primary/5">
        <div className="grid grid-cols-1 items-stretch gap-2 lg:grid-cols-[1fr_auto_1.15fr_auto_1fr] lg:gap-3">
          {/* Sources */}
          <div className="rounded-2xl border border-borderSubtle bg-surfaceSoft p-5">
            <p className="eyebrow mb-1 !text-text-muted">Your systems</p>
            <p className="text-sm font-semibold text-text-primary mb-4">What your team already runs</p>
            <ul className="space-y-2.5">
              {sources.map((item) => (
                <li key={item.label} className="flex items-center gap-3 rounded-xl border border-borderSubtle bg-surface px-3 py-2.5">
                  <item.icon className="h-4 w-4 flex-shrink-0 text-primary" aria-hidden="true" />
                  <span className="text-sm text-text-secondary">{item.label}</span>
                </li>
              ))}
            </ul>
          </div>

          <FlowArrow />

          {/* Contextral automation layer */}
          <div className="relative overflow-hidden rounded-2xl bg-primary p-5 text-white shadow-lg shadow-primary/25">
            <div className="pointer-events-none absolute inset-0 bg-blueprint opacity-20" aria-hidden="true" />
            <div className="relative">
              <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-white/70 mb-1">Contextral</p>
              <p className="text-lg text-white/90 font-bold mb-4">Automation Layer</p>
              <div className="grid grid-cols-3 gap-2">
                {layer.map((item) => (
                  <div key={item.label} className="flex flex-col items-center gap-2 rounded-xl bg-white/10 px-2 py-3 text-center backdrop-blur-sm">
                    <item.icon className="h-5 w-5" aria-hidden="true" />
                    <span className="text-xs font-semibold">{item.label}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs leading-relaxed text-white/80">
                APIs & connectors, workflow orchestration, and instant knowledge retrieval, working as one system for your team.
              </p>
            </div>
          </div>

          <FlowArrow />

          {/* Destinations */}
          <div className="rounded-2xl border border-borderSubtle bg-surfaceSoft p-5">
            <p className="eyebrow mb-1 !text-text-muted">Your data</p>
            <p className="text-sm font-semibold text-text-primary mb-4">What you own and control</p>
            <ul className="space-y-2.5">
              {destinations.map((item) => (
                <li key={item.label} className="flex items-center gap-3 rounded-xl border border-borderSubtle bg-surface px-3 py-2.5">
                  <item.icon className="h-4 w-4 flex-shrink-0 text-primary" aria-hidden="true" />
                  <span className="text-sm text-text-secondary">{item.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Security foundation spans the whole flow. */}
        <div className="mt-3 flex items-center justify-center gap-3 rounded-2xl border border-primary/20 bg-primary/5 px-5 py-3.5">
          <Lock className="h-4 w-4 flex-shrink-0 text-primary" aria-hidden="true" />
          <span className="text-sm font-semibold text-text-primary">
            Full privacy &amp; access control across every workflow
          </span>
        </div>
      </div>
    </section>
  );
}
